/**
 * Длина занятия режима «Основы».
 *
 * Занятие было одной длины всегда — двенадцать элементов, около сорока
 * символов, — и оставалось такой же и на четырёх первых клавишах, и когда
 * открыты все три ряда. На четырёх клавишах это правильно: тянуть новичка
 * дольше минуты по двум движениям значит закреплять усталость. На всех
 * тридцати — это огрызок: человеку уже есть чем заняться, а занятие
 * кончается, не начавшись.
 *
 * Поэтому длина считается не в элементах, а во времени, и время растёт по
 * мере открытия клавиш. Число элементов выводится из того, с какой скоростью
 * человек на самом деле печатает: у медленного и быстрого одинаковое число
 * элементов означало бы разное занятие, а одинаковая минута — одно и то же.
 */

import type { MasteryStat } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import type { LearningConfig } from '@type2x/learning-engine';

import type { FundamentalsSnapshot } from '../contracts/fundamentals.js';
import { MAX_LESSON_ITEMS, MIN_LESSON_ITEMS } from '../contracts/fundamentals.js';

/** Сколько длится занятие, когда открыта только первая группа, с. */
const FIRST_LESSON_SECONDS = 60;

/** Сколько длится занятие, когда открыты все группы, с. */
const FULL_LESSON_SECONDS = 180;

/** Разделитель между элементами — пробел; он тоже набирается. */
const SEPARATOR_LENGTH = 1;

/**
 * Скорость набора по открытым клавишам, мс на нажатие.
 *
 * Берётся медиана по клавишам, которые человек уже набирал. Среднее здесь не
 * годится: одна клавиша, найденная за три секунды, сдвинула бы оценку так,
 * будто он весь текст ищет глазами.
 *
 * Без единой попытки взять неоткуда, и подставляется середина между
 * уверенным нажатием и предельной неуверенностью из конфигурации обучения:
 * это в точности «человек, который клавиши ещё ищет, но не безнадёжно».
 */
export function typingLatencyMs(
  snapshot: FundamentalsSnapshot,
  characters: readonly string[],
  config: LearningConfig,
): number {
  const latencies = characters
    .map((character) => snapshot.stats[toMasteryUnitId(`key:${character}`)])
    .filter((stat): stat is MasteryStat => stat !== undefined && stat.attempts > 0)
    .map((stat) => stat.medianLatencyMs)
    .sort((left, right) => left - right);

  if (latencies.length === 0) {
    return (config.hesitationThresholdMs + config.hesitationCeilingMs) / 2;
  }

  const middle = Math.floor(latencies.length / 2);
  return latencies.length % 2 === 0
    ? ((latencies[middle - 1] ?? 0) + (latencies[middle] ?? 0)) / 2
    : (latencies[middle] ?? 0);
}

/** Сколько времени отводится занятию при таком числе пройденных групп, с. */
export function lessonSeconds(completedGroups: number, totalGroups: number): number {
  if (totalGroups <= 1) return FULL_LESSON_SECONDS;

  const progress = Math.min(completedGroups / (totalGroups - 1), 1);
  return FIRST_LESSON_SECONDS + (FULL_LESSON_SECONDS - FIRST_LESSON_SECONDS) * progress;
}

/**
 * Сколько элементов уместится в занятие.
 *
 * Границы стоят по краям намеренно: ниже нижней занятие не успевает стать
 * занятием, выше верхней — перестаёт быть занятием для новичка. Верхняя
 * совпадает с объёмом пятиминутной персональной тренировки: дольше неё режим
 * первых клавиш быть не должен.
 */
export function lessonItemCount(options: {
  readonly completedGroups: number;
  readonly totalGroups: number;
  /** Средняя длина элемента в символах, без разделителя. */
  readonly itemLength: number;
  readonly latencyMs: number;
}): number {
  const { completedGroups, totalGroups, itemLength, latencyMs } = options;
  const itemMs = (itemLength + SEPARATOR_LENGTH) * latencyMs;
  if (itemMs <= 0) return MIN_LESSON_ITEMS;

  const items = Math.round((lessonSeconds(completedGroups, totalGroups) * 1000) / itemMs);
  return Math.min(Math.max(items, MIN_LESSON_ITEMS), MAX_LESSON_ITEMS);
}
